import React, { useCallback, useMemo } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  type SortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToHorizontalAxis,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";

/**
 * 並び替え可能リストの共通ラッパー。
 *
 * 内部に `DndContext` + `SortableContext` を持ち、senors / modifiers / strategy を
 * `strategy` と `axis` プロパティから自動で組み立てる。並び替えの結果は
 * `onReorder(newOrderedIds)` で通知し、楽観的更新やロールバックは呼び出し側で
 * 行う（API 永続化のポリシーが画面ごとに違うため、ここでは決め打ちしない）。
 *
 * テーブル行・カード・チップなど、表示要素は呼び出し側で完全に自由に書ける。
 * 子要素として `SortableItem` を並べ、各 `SortableItem` の render-prop から得た
 * `setNodeRef` / `handleProps` を実 DOM に流し込む。
 *
 * @example
 * ```tsx
 * <SortableList
 *   items={works}
 *   getId={(w) => String(w.id)}
 *   strategy="vertical"
 *   axis="y"
 *   onReorder={async (newIds) => {
 *     setLocal(reorderFromIds(local, newIds));
 *     try { await mutate(newIds).unwrap(); }
 *     catch { rollback(); }
 *   }}
 * >
 *   {works.map((w) => (
 *     <SortableItem key={w.id} id={String(w.id)}>
 *       {({ setNodeRef, style, handleProps }) => (
 *         <tr ref={setNodeRef} style={style}>
 *           <td><SortableDragHandle {...handleProps} /></td>
 *           <td>{w.title}</td>
 *         </tr>
 *       )}
 *     </SortableItem>
 *   ))}
 * </SortableList>
 * ```
 */
export interface SortableListProps<T> {
  /** 並び替え対象のアイテム配列（順序は表示順と一致させる）。 */
  items: T[];
  /**
   * アイテムから DnD 用の一意 ID を取り出す関数。
   * 省略時は `item.id` を文字列化したものを使う（`id` プロパティを持つ場合）。
   */
  // eslint-disable-next-line no-unused-vars
  getId?: (item: T) => string;
  /** 並び替え戦略。テーブル行・縦リストなら "vertical"、横スクロール一覧やチップ列なら "horizontal"、グリッド型は "grid"。既定 "vertical"。 */
  strategy?: "vertical" | "horizontal" | "grid";
  /**
   * ドラッグの軸制約。"y" は縦方向のみ、"x" は横方向のみ、null は制約なし（グリッド向け）。
   * 省略時は `strategy` から推定（vertical → y / horizontal → x / grid → null）。
   */
  axis?: "x" | "y" | null;
  /** リスト全体で並び替えを無効化する（例: タグでフィルタ中など、表示順が一部しか見えない場面）。 */
  disabled?: boolean;
  /**
   * 並び替え確定時に呼ばれる。新しい順序の `id` 配列を受け取る。
   * 楽観的更新・API 永続化・失敗時のロールバックは呼び出し側で行う。
   */
  // eslint-disable-next-line no-unused-vars
  onReorder: (newOrderedIds: string[]) => void | Promise<void>;
  children: React.ReactNode;
}

const STRATEGY_MAP: Record<NonNullable<SortableListProps<unknown>["strategy"]>, SortingStrategy> = {
  vertical: verticalListSortingStrategy,
  horizontal: horizontalListSortingStrategy,
  grid: rectSortingStrategy,
};

const AXIS_MODIFIER: Record<"x" | "y", Modifier> = {
  x: restrictToHorizontalAxis,
  y: restrictToVerticalAxis,
};

const defaultGetId = <T,>(item: T): string => {
  const id = (item as { id?: unknown })?.id;
  return id == null ? "" : String(id);
};

function SortableList<T>({
  items,
  getId,
  strategy = "vertical",
  axis,
  disabled = false,
  onReorder,
  children,
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const resolveId = getId ?? defaultGetId<T>;

  const ids = useMemo(() => items.map((it) => resolveId(it)), [items, resolveId]);

  const resolvedAxis: "x" | "y" | null =
    axis !== undefined
      ? axis
      : strategy === "vertical"
        ? "y"
        : strategy === "horizontal"
          ? "x"
          : null;

  const modifiers: Modifier[] = useMemo(
    () => (resolvedAxis ? [AXIS_MODIFIER[resolvedAxis]] : []),
    [resolvedAxis],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (disabled) return;
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = ids.findIndex((id) => id === String(active.id));
      const newIndex = ids.findIndex((id) => id === String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;
      const reordered = arrayMove(ids, oldIndex, newIndex);
      void onReorder(reordered);
    },
    [disabled, ids, onReorder],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={modifiers}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={STRATEGY_MAP[strategy]} disabled={disabled}>
        {children}
      </SortableContext>
    </DndContext>
  );
}

export default SortableList;
